import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  FontAwesomeIcon,
} from "@fortawesome/react-fontawesome";

import {
  faArrowRotateRight,
  faBoxesStacked,
  faSearch,
  faWarehouse,
} from "@fortawesome/free-solid-svg-icons";

import toast from "react-hot-toast";

import {
  inventoryTransfersApi,
} from "../api/inventoryTransfersApi";

import {
  inventoryApi,
} from "../api/inventoryApi";

import {
  warehousesApi,
} from "../api/warehousesApi";

import {
  productsApi,
} from "../api/productsApi";

import {
  useAuth,
} from "../contexts/AuthContext";

import type {
  InventoryTransferRecord,
} from "../types/inventory-transfers.types";

import type {
  WarehouseRecord,
  WarehouseStockRecord,
} from "../types/warehouses.types";

import type {
  AdminProduct,
} from "../types/products.types";

import "../screenstyles/InventoryTransfers.css";


type TransferFormState = {
  productId: string;

  sourceWarehouseId: string;

  targetWarehouseId: string;

  quantity: string;

  reason: string;
};


type HistoryFilters = {
  productId: string;

  sourceWarehouseId: string;

  targetWarehouseId: string;
};


const EMPTY_FORM:
TransferFormState = {
  productId: "",

  sourceWarehouseId: "",

  targetWarehouseId: "",

  quantity: "",

  reason: "",
};


const EMPTY_HISTORY_FILTERS:
HistoryFilters = {
  productId: "",

  sourceWarehouseId: "",

  targetWarehouseId: "",
};


function getErrorCode(
  error: unknown,
): string {
  if (
    error &&
    typeof error === "object" &&
    "code" in error
  ) {
    return String(
      (
        error as {
          code?: unknown;
        }
      ).code ?? "",
    );
  }

  return "";
}


function getErrorMessage(
  error: unknown,
): string {
  const code =
    getErrorCode(
      error,
    );

  switch (code) {
    case "WAREHOUSE_INVENTORY_DISABLED":
      return "نظام مخزون المستودعات غير مفعل.";

    case "WAREHOUSE_TRANSFER_SAME_WAREHOUSE":
      return "لا يمكن أن يكون مستودع المصدر والهدف متطابقين.";

    case "SOURCE_WAREHOUSE_NOT_FOUND":
      return "مستودع المصدر غير موجود.";

    case "TARGET_WAREHOUSE_NOT_FOUND":
      return "مستودع الهدف غير موجود.";

    case "SOURCE_WAREHOUSE_INACTIVE":
      return "مستودع المصدر غير فعال.";

    case "TARGET_WAREHOUSE_INACTIVE":
      return "مستودع الهدف غير فعال.";

    case "SOURCE_WAREHOUSE_STOCK_NOT_FOUND":
      return "هذا المنتج غير موجود في مخزون مستودع المصدر.";

    case "INSUFFICIENT_SOURCE_WAREHOUSE_STOCK":
      return "الكمية المتاحة في مستودع المصدر غير كافية.";

    case "INVALID_TRANSFER_QUANTITY":
      return "كمية النقل يجب أن تكون رقمًا صحيحًا أكبر من صفر.";

    case "INVENTORY_REASON_REQUIRED":
      return "سبب النقل مطلوب.";

    case "WAREHOUSE_TRANSFER_IDEMPOTENCY_CONFLICT":
      return "حدث تعارض في مفتاح حماية عملية النقل. أعد المحاولة.";

    case "WAREHOUSE_TRANSFER_NOT_FOUND":
      return "عملية النقل غير موجودة.";

    case "WAREHOUSE_STOCK_NOT_FOUND":
      return "لا يوجد سجل مخزون لهذا المنتج في المستودع.";
  }


  if (
    error instanceof Error &&
    error.message
  ) {
    return error.message;
  }


  return "حدث خطأ غير متوقع.";
}


function formatDate(
  value: unknown,
): string {
  if (
    !value ||
    typeof value !== "object"
  ) {
    return "—";
  }


  const timestamp =
    value as {
      _seconds?: number;
      seconds?: number;
    };


  const seconds =
    timestamp._seconds ??
    timestamp.seconds;


  if (
    typeof seconds !== "number"
  ) {
    return "—";
  }


  return new Intl.DateTimeFormat(
    "ar-IQ",
    {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    },
  ).format(
    new Date(
      seconds * 1000,
    ),
  );
}


function createIdempotencyKey():
string {
  const randomValues =
    new Uint32Array(
      4,
    );


  window.crypto.getRandomValues(
    randomValues,
  );


  return [
    "dashboard-transfer",
    Date.now(),
    ...Array.from(
      randomValues,
    ),
  ].join("-");
}


const InventoryTransfers:
React.FC = () => {

  const {
    hasPermission,
  } = useAuth();


  const canView =
    hasPermission(
      "inventory.transfers.view",
    );


  const canCreate =
    hasPermission(
      "inventory.transfers.create",
    );


  /* =======================================================
   * Warehouses
   * ===================================================== */

  const [
    warehouses,
    setWarehouses,
  ] =
    useState<
      WarehouseRecord[]
    >([]);


  const [
    warehousesLoading,
    setWarehousesLoading,
  ] =
    useState(false);


  /* =======================================================
   * Products
   * ===================================================== */

  const [
    products,
    setProducts,
  ] =
    useState<
      AdminProduct[]
    >([]);


  const [
    productsLoading,
    setProductsLoading,
  ] =
    useState(false);


  const [
    productSearch,
    setProductSearch,
  ] =
    useState("");


  const [
    selectedProduct,
    setSelectedProduct,
  ] =
    useState<
      AdminProduct | null
    >(null);


  /* =======================================================
   * Transfer Form
   * ===================================================== */

  const [
    form,
    setForm,
  ] =
    useState<
      TransferFormState
    >(
      EMPTY_FORM,
    );


  const [
    idempotencyKey,
    setIdempotencyKey,
  ] =
    useState(
      createIdempotencyKey,
    );


  const [
    submitting,
    setSubmitting,
  ] =
    useState(false);


  /* =======================================================
   * Warehouse Stocks
   * ===================================================== */

  const [
    sourceStock,
    setSourceStock,
  ] =
    useState<
      WarehouseStockRecord | null
    >(null);


  const [
    targetStock,
    setTargetStock,
  ] =
    useState<
      WarehouseStockRecord | null
    >(null);


  const [
    stocksLoading,
    setStocksLoading,
  ] =
    useState(false);


  /* =======================================================
   * Transfer History
   * ===================================================== */

  const [
    transfers,
    setTransfers,
  ] =
    useState<
      InventoryTransferRecord[]
    >([]);


  const [
    historyLoading,
    setHistoryLoading,
  ] =
    useState(false);


  const [
    historyLoadingMore,
    setHistoryLoadingMore,
  ] =
    useState(false);


  const [
    historyNextCursor,
    setHistoryNextCursor,
  ] =
    useState<
      string | null
    >(null);


  const [
    historyFilters,
    setHistoryFilters,
  ] =
    useState<
      HistoryFilters
    >(
      EMPTY_HISTORY_FILTERS,
    );


  /* =======================================================
   * Details
   * ===================================================== */

  const [
    selectedTransfer,
    setSelectedTransfer,
  ] =
    useState<
      InventoryTransferRecord | null
    >(null);


  const [
    detailsLoading,
    setDetailsLoading,
  ] =
    useState(false);


  /* =======================================================
   * Helpers
   * ===================================================== */

  const warehouseMap =
    useMemo(
      () =>
        new Map(
          warehouses.map(
            warehouse => [
              warehouse.id,
              warehouse,
            ],
          ),
        ),
      [
        warehouses,
      ],
    );


  const getWarehouseName =
    useCallback(
      (
        warehouseId: string,
      ) => {
        const warehouse =
          warehouseMap.get(
            warehouseId,
          );


        return (
          warehouse?.nameAr ||
          warehouse?.name ||
          warehouseId
        );
      },
      [
        warehouseMap,
      ],
    );


  const loadWarehouses =
    useCallback(
      async () => {
        if (!canView) {
          return;
        }


        try {
          setWarehousesLoading(
            true,
          );


          const result =
            await warehousesApi
              .list({
                isActive: true,

                sort:
                  "name_asc",

                limit: 100,
              });


          setWarehouses(
            result.items,
          );


          setForm(
            current => {
              if (
                current
                  .sourceWarehouseId
              ) {
                return current;
              }


              const defaultWarehouse =
                result.items.find(
                  item =>
                    item.isDefault,
                ) ??
                result.items[0];


              if (
                !defaultWarehouse
              ) {
                return current;
              }


              return {
                ...current,

                sourceWarehouseId:
                  defaultWarehouse.id,
              };
            },
          );
        } catch (error) {
          console.error(
            "Failed to load warehouses:",
            error,
          );


          toast.error(
            getErrorMessage(
              error,
            ),
          );
        } finally {
          setWarehousesLoading(
            false,
          );
        }
      },
      [
        canView,
      ],
    );


  const loadProducts =
    useCallback(
      async () => {
        if (!canView) {
          return;
        }


        try {
          setProductsLoading(
            true,
          );


          const result =
            await productsApi
              .adminList({
                search:
                  productSearch
                    .trim() ||
                  undefined,

                isDeleted:
                  false,

                sort:
                  "createdAt_desc",

                limit:
                  50,
              });


          setProducts(
            result.items,
          );
        } catch (error) {
          console.error(
            "Failed to load products:",
            error,
          );


          toast.error(
            getErrorMessage(
              error,
            ),
          );
        } finally {
          setProductsLoading(
            false,
          );
        }
      },
      [
        canView,
        productSearch,
      ],
    );


  const readWarehouseStock =
    useCallback(
      async (
        warehouseId: string,

        productId: string,
      ):
      Promise<
        WarehouseStockRecord | null
      > => {
        if (
          !warehouseId ||
          !productId
        ) {
          return null;
        }


        try {
          return await inventoryApi
            .getWarehouseProductStock(
              warehouseId,
              productId,
            );
        } catch (error) {
          if (
            getErrorCode(
              error,
            ) ===
            "WAREHOUSE_STOCK_NOT_FOUND"
          ) {
            return null;
          }


          throw error;
        }
      },
      [],
    );


  const loadSelectedStocks =
    useCallback(
      async () => {
        if (
          !form.productId
        ) {
          setSourceStock(
            null,
          );

          setTargetStock(
            null,
          );

          return;
        }


        try {
          setStocksLoading(
            true,
          );


          const [
            sourceResult,
            targetResult,
          ] =
            await Promise.all([
              form
                .sourceWarehouseId
                ? readWarehouseStock(
                    form
                      .sourceWarehouseId,
                    form
                      .productId,
                  )
                : Promise.resolve(
                    null,
                  ),

              form
                .targetWarehouseId
                ? readWarehouseStock(
                    form
                      .targetWarehouseId,
                    form
                      .productId,
                  )
                : Promise.resolve(
                    null,
                  ),
            ]);


          setSourceStock(
            sourceResult,
          );

          setTargetStock(
            targetResult,
          );
        } catch (error) {
          console.error(
            "Failed to load warehouse stocks:",
            error,
          );


          setSourceStock(
            null,
          );

          setTargetStock(
            null,
          );


          toast.error(
            getErrorMessage(
              error,
            ),
          );
        } finally {
          setStocksLoading(
            false,
          );
        }
      },
      [
        form.productId,
        form
          .sourceWarehouseId,
        form
          .targetWarehouseId,
        readWarehouseStock,
      ],
    );


  const loadHistory =
    useCallback(
      async (
        append = false,

        cursor?: string,
      ) => {
        if (!canView) {
          return;
        }


        try {
          if (append) {
            setHistoryLoadingMore(
              true,
            );
          } else {
            setHistoryLoading(
              true,
            );
          }


          const result =
            await inventoryTransfersApi
              .list({
                productId:
                  historyFilters
                    .productId
                    .trim() ||
                  undefined,

                sourceWarehouseId:
                  historyFilters
                    .sourceWarehouseId ||
                  undefined,

                targetWarehouseId:
                  historyFilters
                    .targetWarehouseId ||
                  undefined,

                cursor,

                limit:
                  30,
              });


          setTransfers(
            current =>
              append
                ? [
                    ...current,
                    ...result.items,
                  ]
                : result.items,
          );


          setHistoryNextCursor(
            result.pagination
              .nextCursor,
          );
        } catch (error) {
          console.error(
            "Failed to load transfer history:",
            error,
          );


          toast.error(
            getErrorMessage(
              error,
            ),
          );
        } finally {
          setHistoryLoading(
            false,
          );

          setHistoryLoadingMore(
            false,
          );
        }
      },
      [
        canView,
        historyFilters,
      ],
    );


  /* =======================================================
   * Effects
   * ===================================================== */

  useEffect(
    () => {
      loadWarehouses();
    },
    [
      loadWarehouses,
    ],
  );


  useEffect(
    () => {
      const timer =
        window.setTimeout(
          () => {
            loadProducts();
          },
          300,
        );


      return () => {
        window.clearTimeout(
          timer,
        );
      };
    },
    [
      loadProducts,
    ],
  );


  useEffect(
    () => {
      loadSelectedStocks();
    },
    [
      loadSelectedStocks,
    ],
  );


  useEffect(
    () => {
      loadHistory();
    },
    [
      loadHistory,
    ],
  );


  /* =======================================================
   * Form
   * ===================================================== */

  const updateForm =
    (
      field:
        keyof TransferFormState,

      value:
        string,
    ) => {
      setForm(
        current => ({
          ...current,

          [field]:
            value,
        }),
      );
    };


  const handleProductChange =
    (
      productId:
        string,
    ) => {
      const product =
        products.find(
          item =>
            item.id ===
            productId,
        ) ??
        null;


      setSelectedProduct(
        product,
      );


      updateForm(
        "productId",
        productId,
      );
    };


  const handleSubmit =
    async (
      event:
        React.FormEvent,
    ) => {
      event.preventDefault();


      if (!canCreate) {
        toast.error(
          "ليس لديك صلاحية لتنفيذ نقل مخزون.",
        );

        return;
      }


      if (
        !form.productId
      ) {
        toast.error(
          "يرجى اختيار المنتج.",
        );

        return;
      }


      if (
        !form
          .sourceWarehouseId ||
        !form
          .targetWarehouseId
      ) {
        toast.error(
          "يرجى اختيار مستودع المصدر والهدف.",
        );

        return;
      }


      if (
        form
          .sourceWarehouseId ===
        form
          .targetWarehouseId
      ) {
        toast.error(
          "مستودع المصدر والهدف يجب أن يكونا مختلفين.",
        );

        return;
      }


      const quantity =
        Number(
          form.quantity,
        );


      if (
        !Number.isInteger(
          quantity,
        ) ||
        quantity <= 0
      ) {
        toast.error(
          "كمية النقل يجب أن تكون رقمًا صحيحًا أكبر من صفر.",
        );

        return;
      }


      if (
        !sourceStock
      ) {
        toast.error(
          "لا يوجد مخزون لهذا المنتج في مستودع المصدر.",
        );

        return;
      }


      if (
        quantity >
        sourceStock.quantity
      ) {
        toast.error(
          `الكمية المتاحة في المصدر هي ${sourceStock.quantity} فقط.`,
        );

        return;
      }


      if (
        !form.reason.trim()
      ) {
        toast.error(
          "يرجى إدخال سبب النقل.",
        );

        return;
      }


      const sourceName =
        getWarehouseName(
          form
            .sourceWarehouseId,
        );


      const targetName =
        getWarehouseName(
          form
            .targetWarehouseId,
        );


      const confirmed =
        window.confirm(
          `سيتم نقل ${quantity} قطعة من "${sourceName}" إلى "${targetName}". هل تريد المتابعة؟`,
        );


      if (!confirmed) {
        return;
      }


      try {
        setSubmitting(
          true,
        );


        const result =
          await inventoryTransfersApi
            .create({
              productId:
                form.productId,

              sourceWarehouseId:
                form
                  .sourceWarehouseId,

              targetWarehouseId:
                form
                  .targetWarehouseId,

              quantity,

              reason:
                form.reason
                  .trim(),

              idempotencyKey,
            });


        if (
          result.idempotent
        ) {
          toast.success(
            "تم العثور على العملية المنفذة مسبقًا ولم يتم تكرار النقل.",
          );
        } else {
          toast.success(
            "تم نقل المخزون بنجاح.",
          );
        }


        setIdempotencyKey(
          createIdempotencyKey(),
        );


        setForm(
          current => ({
            ...current,

            quantity:
              "",

            reason:
              "",
          }),
        );


        await Promise.all([
          loadSelectedStocks(),

          loadHistory(),
        ]);
      } catch (error) {
        console.error(
          "Failed to create inventory transfer:",
          error,
        );


        toast.error(
          getErrorMessage(
            error,
          ),
        );
      } finally {
        setSubmitting(
          false,
        );
      }
    };


  /* =======================================================
   * Details
   * ===================================================== */

  const openTransferDetails =
    async (
      transferId: string,
    ) => {
      try {
        setDetailsLoading(
          true,
        );


        const result =
          await inventoryTransfersApi
            .getById(
              transferId,
            );


        setSelectedTransfer(
          result.transfer,
        );
      } catch (error) {
        console.error(
          "Failed to load transfer details:",
          error,
        );


        toast.error(
          getErrorMessage(
            error,
          ),
        );
      } finally {
        setDetailsLoading(
          false,
        );
      }
    };


  /* =======================================================
   * Permissions
   * ===================================================== */

  if (!canView) {
    return (
      <div
        className="inventory-transfers-page"
        dir="rtl"
      >
        <div className="inventory-transfers-no-access">
          ليس لديك صلاحية لعرض عمليات نقل المخزون.
        </div>
      </div>
    );
  }


  return (
    <div
      className="inventory-transfers-page"
      dir="rtl"
    >

      {/* ===================================================
       * Header
       * ================================================= */}

      <div className="inventory-transfers-header">

        <div>
          <div className="inventory-transfers-title">

            <FontAwesomeIcon
              icon={
                faWarehouse
              }
            />

            <h1>
              نقل المخزون
            </h1>

          </div>

          <p>
            نقل الكمية المتاحة بين مستودعات الشركة بدون تغيير إجمالي مخزون المنتج.
          </p>
        </div>


        <button
          type="button"
          className="inventory-transfers-refresh-btn"
          onClick={() => {
            loadSelectedStocks();

            loadHistory();
          }}
          disabled={
            stocksLoading ||
            historyLoading
          }
        >
          <FontAwesomeIcon
            icon={
              faArrowRotateRight
            }
          />

          تحديث
        </button>

      </div>


      {/* ===================================================
       * Create Transfer
       * ================================================= */}

      <section className="inventory-transfers-card">

        <div className="inventory-transfers-section-title">

          <FontAwesomeIcon
            icon={
              faBoxesStacked
            }
          />

          <div>
            <h2>
              إنشاء عملية نقل
            </h2>

            <p>
              يتم نقل الكمية المتاحة فقط، ولا يتم نقل الكميات المحجوزة.
            </p>
          </div>

        </div>


        <form
          className="inventory-transfers-form"
          onSubmit={
            handleSubmit
          }
        >

          <label className="inventory-transfers-field inventory-transfers-field-full">

            <span>
              البحث عن المنتج
            </span>

            <div className="inventory-transfers-search">

              <FontAwesomeIcon
                icon={
                  faSearch
                }
              />

              <input
                value={
                  productSearch
                }
                onChange={
                  event =>
                    setProductSearch(
                      event.target
                        .value,
                    )
                }
                placeholder="ابحث باسم المنتج أو الكود..."
              />

            </div>

          </label>


          <label className="inventory-transfers-field inventory-transfers-field-full">

            <span>
              المنتج
            </span>

            <select
              value={
                form.productId
              }
              onChange={
                event =>
                  handleProductChange(
                    event.target
                      .value,
                  )
              }
              disabled={
                productsLoading
              }
            >

              <option value="">
                {
                  productsLoading
                    ? "جارٍ تحميل المنتجات..."
                    : "اختر المنتج"
                }
              </option>


              {selectedProduct &&
                !products.some(
                  item =>
                    item.id ===
                    selectedProduct.id,
                ) && (
                  <option
                    value={
                      selectedProduct.id
                    }
                  >
                    {selectedProduct.code} — {selectedProduct.name}
                  </option>
                )}


              {products.map(
                product => (
                  <option
                    key={
                      product.id
                    }
                    value={
                      product.id
                    }
                  >
                    {product.code} — {product.name}
                  </option>
                ),
              )}

            </select>

          </label>


          <label className="inventory-transfers-field">

            <span>
              مستودع المصدر
            </span>

            <select
              value={
                form
                  .sourceWarehouseId
              }
              onChange={
                event =>
                  updateForm(
                    "sourceWarehouseId",
                    event.target
                      .value,
                  )
              }
              disabled={
                warehousesLoading
              }
            >

              <option value="">
                اختر مستودع المصدر
              </option>


              {warehouses.map(
                warehouse => (
                  <option
                    key={
                      warehouse.id
                    }
                    value={
                      warehouse.id
                    }
                  >
                    {warehouse.nameAr || warehouse.name} ({warehouse.code})
                  </option>
                ),
              )}

            </select>

          </label>


          <label className="inventory-transfers-field">

            <span>
              مستودع الهدف
            </span>

            <select
              value={
                form
                  .targetWarehouseId
              }
              onChange={
                event =>
                  updateForm(
                    "targetWarehouseId",
                    event.target
                      .value,
                  )
              }
              disabled={
                warehousesLoading
              }
            >

              <option value="">
                اختر مستودع الهدف
              </option>


              {warehouses
                .filter(
                  warehouse =>
                    warehouse.id !==
                    form
                      .sourceWarehouseId,
                )
                .map(
                  warehouse => (
                    <option
                      key={
                        warehouse.id
                      }
                      value={
                        warehouse.id
                      }
                    >
                      {warehouse.nameAr || warehouse.name} ({warehouse.code})
                    </option>
                  ),
                )}

            </select>

          </label>


          <div className="inventory-transfers-stock-summary">

            <div>
              <span>
                المتاح في المصدر
              </span>

              <strong>
                {
                  stocksLoading
                    ? "..."
                    : sourceStock
                      ?.quantity ??
                      0
                }
              </strong>
            </div>


            <div>
              <span>
                المحجوز في المصدر
              </span>

              <strong>
                {
                  stocksLoading
                    ? "..."
                    : sourceStock
                      ?.reservedQuantity ??
                      0
                }
              </strong>
            </div>


            <div>
              <span>
                الفعلي في المصدر
              </span>

              <strong>
                {
                  stocksLoading
                    ? "..."
                    : sourceStock
                      ?.onHandQuantity ??
                      0
                }
              </strong>
            </div>


            <div>
              <span>
                المتاح في الهدف
              </span>

              <strong>
                {
                  stocksLoading
                    ? "..."
                    : targetStock
                      ?.quantity ??
                      0
                }
              </strong>
            </div>

          </div>


          <label className="inventory-transfers-field">

            <span>
              كمية النقل
            </span>

            <input
              type="number"
              min="1"
              step="1"
              max={
                sourceStock
                  ?.quantity ||
                undefined
              }
              value={
                form.quantity
              }
              onChange={
                event =>
                  updateForm(
                    "quantity",
                    event.target
                      .value,
                  )
              }
              placeholder="مثال: 10"
            />

          </label>


          <label className="inventory-transfers-field inventory-transfers-field-full">

            <span>
              سبب النقل
            </span>

            <textarea
              value={
                form.reason
              }
              onChange={
                event =>
                  updateForm(
                    "reason",
                    event.target
                      .value,
                  )
              }
              maxLength={
                500
              }
              placeholder="اكتب سبب نقل المخزون..."
            />

          </label>


          <div className="inventory-transfers-warning inventory-transfers-field-full">
            <strong>
              ملاحظة:
            </strong>

            عملية النقل تغيّر مخزون المستودعين فقط ولا تغيّر
            إجمالي <code>Product.quantity</code>.
            كما أن <code>reservedQuantity</code> لا يتم نقله.
          </div>


          <div className="inventory-transfers-submit-row inventory-transfers-field-full">

            <button
              type="submit"
              className="inventory-transfers-submit-btn"
              disabled={
                !canCreate ||
                submitting ||
                stocksLoading
              }
            >
              {
                submitting
                  ? "جارٍ تنفيذ النقل..."
                  : "تنفيذ نقل المخزون"
              }
            </button>

          </div>

        </form>

      </section>


      {/* ===================================================
       * History
       * ================================================= */}

      <section className="inventory-transfers-card">

        <div className="inventory-transfers-section-title">

          <FontAwesomeIcon
            icon={
              faArrowRotateRight
            }
          />

          <div>
            <h2>
              سجل عمليات النقل
            </h2>

            <p>
              جميع عمليات نقل المخزون بين المستودعات.
            </p>
          </div>

        </div>


        <div className="inventory-transfers-history-filters">

          <input
            value={
              historyFilters
                .productId
            }
            onChange={
              event =>
                setHistoryFilters(
                  current => ({
                    ...current,

                    productId:
                      event.target
                        .value,
                  }),
                )
            }
            placeholder="Product ID"
          />


          <select
            value={
              historyFilters
                .sourceWarehouseId
            }
            onChange={
              event =>
                setHistoryFilters(
                  current => ({
                    ...current,

                    sourceWarehouseId:
                      event.target
                        .value,
                  }),
                )
            }
          >

            <option value="">
              كل مستودعات المصدر
            </option>

            {warehouses.map(
              warehouse => (
                <option
                  key={
                    warehouse.id
                  }
                  value={
                    warehouse.id
                  }
                >
                  {warehouse.nameAr || warehouse.name}
                </option>
              ),
            )}

          </select>


          <select
            value={
              historyFilters
                .targetWarehouseId
            }
            onChange={
              event =>
                setHistoryFilters(
                  current => ({
                    ...current,

                    targetWarehouseId:
                      event.target
                        .value,
                  }),
                )
            }
          >

            <option value="">
              كل مستودعات الهدف
            </option>

            {warehouses.map(
              warehouse => (
                <option
                  key={
                    warehouse.id
                  }
                  value={
                    warehouse.id
                  }
                >
                  {warehouse.nameAr || warehouse.name}
                </option>
              ),
            )}

          </select>


          <button
            type="button"
            onClick={() => {
              setHistoryFilters(
                EMPTY_HISTORY_FILTERS,
              );
            }}
          >
            مسح الفلاتر
          </button>

        </div>


        {historyLoading ? (

          <div className="inventory-transfers-loading">
            جارٍ تحميل سجل عمليات النقل...
          </div>

        ) : transfers.length === 0 ? (

          <div className="inventory-transfers-empty">
            لا توجد عمليات نقل مطابقة.
          </div>

        ) : (

          <>

            <div className="inventory-transfers-table-wrapper">

              <table className="inventory-transfers-table">

                <thead>
                  <tr>
                    <th>
                      المنتج
                    </th>

                    <th>
                      المصدر
                    </th>

                    <th>
                      الهدف
                    </th>

                    <th>
                      الكمية
                    </th>

                    <th>
                      منفذ العملية
                    </th>

                    <th>
                      التاريخ
                    </th>

                    <th>
                      التفاصيل
                    </th>
                  </tr>
                </thead>


                <tbody>

                  {transfers.map(
                    transfer => (
                      <tr
                        key={
                          transfer
                            .transferId
                        }
                      >

                        <td>
                          <div className="inventory-transfers-product-cell">

                            <strong>
                              {
                                transfer
                                  .productName
                              }
                            </strong>

                            <span>
                              {
                                transfer
                                  .productCode ||
                                transfer
                                  .productId
                              }
                            </span>

                          </div>
                        </td>


                        <td>
                          {
                            getWarehouseName(
                              transfer
                                .sourceWarehouseId,
                            )
                          }

                          <small className="inventory-transfers-quantity-change">
                            {transfer.sourcePreviousQuantity} → {transfer.sourceNewQuantity}
                          </small>
                        </td>


                        <td>
                          {
                            getWarehouseName(
                              transfer
                                .targetWarehouseId,
                            )
                          }

                          <small className="inventory-transfers-quantity-change">
                            {transfer.targetPreviousQuantity} → {transfer.targetNewQuantity}
                          </small>
                        </td>


                        <td>
                          <strong className="inventory-transfers-quantity-badge">
                            {
                              transfer
                                .quantity
                            }
                          </strong>
                        </td>


                        <td>
                          {
                            transfer
                              .performedByEmail ||
                            transfer
                              .performedBy
                          }
                        </td>


                        <td>
                          {
                            formatDate(
                              transfer
                                .createdAt,
                            )
                          }
                        </td>


                        <td>
                          <button
                            type="button"
                            className="inventory-transfers-details-btn"
                            disabled={
                              detailsLoading
                            }
                            onClick={() =>
                              openTransferDetails(
                                transfer
                                  .transferId,
                              )
                            }
                          >
                            <FontAwesomeIcon
                              icon={
                                faSearch
                              }
                            />

                            عرض
                          </button>
                        </td>

                      </tr>
                    ),
                  )}

                </tbody>

              </table>

            </div>


            {historyNextCursor && (
              <button
                type="button"
                className="inventory-transfers-load-more"
                disabled={
                  historyLoadingMore
                }
                onClick={() =>
                  loadHistory(
                    true,
                    historyNextCursor,
                  )
                }
              >
                {
                  historyLoadingMore
                    ? "جارٍ التحميل..."
                    : "تحميل المزيد"
                }
              </button>
            )}

          </>

        )}

      </section>


      {/* ===================================================
       * Details Modal
       * ================================================= */}

      {selectedTransfer && (

        <div
          className="inventory-transfers-modal-backdrop"
          onMouseDown={
            event => {
              if (
                event.target ===
                event.currentTarget
              ) {
                setSelectedTransfer(
                  null,
                );
              }
            }
          }
        >

          <div className="inventory-transfers-modal">

            <div className="inventory-transfers-modal-header">

              <div>
                <h2>
                  تفاصيل عملية النقل
                </h2>

                <p>
                  {
                    selectedTransfer
                      .transferId
                  }
                </p>
              </div>


              <button
                type="button"
                onClick={() =>
                  setSelectedTransfer(
                    null,
                  )
                }
              >
                ×
              </button>

            </div>


            <div className="inventory-transfers-details-grid">

              <div>
                <span>
                  المنتج
                </span>

                <strong>
                  {
                    selectedTransfer
                      .productName
                  }
                </strong>

                <small>
                  {
                    selectedTransfer
                      .productCode
                  }
                </small>
              </div>


              <div>
                <span>
                  الكمية المنقولة
                </span>

                <strong>
                  {
                    selectedTransfer
                      .quantity
                  }
                </strong>
              </div>


              <div>
                <span>
                  مستودع المصدر
                </span>

                <strong>
                  {
                    getWarehouseName(
                      selectedTransfer
                        .sourceWarehouseId,
                    )
                  }
                </strong>

                <small>
                  {
                    selectedTransfer
                      .sourcePreviousQuantity
                  }
                  {" → "}
                  {
                    selectedTransfer
                      .sourceNewQuantity
                  }
                </small>
              </div>


              <div>
                <span>
                  مستودع الهدف
                </span>

                <strong>
                  {
                    getWarehouseName(
                      selectedTransfer
                        .targetWarehouseId,
                    )
                  }
                </strong>

                <small>
                  {
                    selectedTransfer
                      .targetPreviousQuantity
                  }
                  {" → "}
                  {
                    selectedTransfer
                      .targetNewQuantity
                  }
                </small>
              </div>


              <div>
                <span>
                  المحجوز في المصدر
                </span>

                <strong>
                  {
                    selectedTransfer
                      .sourceReservedQuantity
                  }
                </strong>
              </div>


              <div>
                <span>
                  المحجوز في الهدف
                </span>

                <strong>
                  {
                    selectedTransfer
                      .targetReservedQuantity
                  }
                </strong>
              </div>


              <div>
                <span>
                  إجمالي مخزون المنتج
                </span>

                <strong>
                  {
                    selectedTransfer
                      .productQuantity
                  }
                </strong>
              </div>


              <div>
                <span>
                  منفذ العملية
                </span>

                <strong>
                  {
                    selectedTransfer
                      .performedByEmail ||
                    selectedTransfer
                      .performedBy
                  }
                </strong>
              </div>


              <div className="inventory-transfers-details-full">
                <span>
                  سبب النقل
                </span>

                <strong>
                  {
                    selectedTransfer
                      .reason
                  }
                </strong>
              </div>


              <div className="inventory-transfers-details-full">
                <span>
                  التاريخ
                </span>

                <strong>
                  {
                    formatDate(
                      selectedTransfer
                        .createdAt,
                    )
                  }
                </strong>
              </div>

            </div>

          </div>

        </div>

      )}

    </div>
  );
};


export default InventoryTransfers;